// Selection inspector operational pattern (DESIGN_SYSTEM.md §10.1, §10.5, WORKSPACE_COCKPIT_SPEC.md §10.1).
// Provides 3 presentation modes: dock (non-modal side panel), drawer (modal sheet),
// and full (modal mobile view with Back navigation). Supports Summary, Evidence, and
// Activity tabs for checks, events, findings, evidence, decisions, scope, and plan.

import { useMemo } from 'react';
import { X, ArrowLeft, FileText, Check, HelpCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';

import { Badge } from './Badge.tsx';
import { Button } from './Button.tsx';
import { EvidenceExcerpt } from './EvidenceExcerpt.tsx';
import { Tabs, Tab, TabList, TabPanel } from './Tabs.tsx';
import {
  StateLabel,
  TruthLabel,
  type OriginState,
  type RemediationState,
  type RetestState,
  type ReviewState,
  type SeverityState,
} from './TruthLabel.tsx';
import type { RunEvent, RunView } from '../features/engagement/types.ts';
import { clockTime, eventTime, STEP_STATE_LABEL } from '../features/engagement/model.ts';
import { Disclosure } from './Disclosure.tsx';
import { formatDateTime, shortId } from './format.ts';
import {
  collectorLabel,
  collectorText,
  neutralPointer,
} from '../features/discovery/collectorLabel.ts';
import styles from './Inspector.module.css';

export type InspectorKind =
  'check' | 'event' | 'finding' | 'evidence' | 'scope' | 'plan' | 'decision';

export interface InspectorSelection {
  kind: InspectorKind;
  id: string;
}

export interface FindingDetail {
  id: string;
  title: string;
  severity: SeverityState;
  origin: OriginState;
  review: ReviewState;
  remediation?: RemediationState;
  retest?: RetestState;
  affectedAssets: string[];
  description: string;
  expected?: string;
  observed?: string;
  evidenceIds?: string[];
  limitations?: string;
}

export interface EvidenceTransformation {
  transformationId: string;
  class: 'DETERMINISTIC' | 'HUMAN' | 'MODEL_ASSISTED' | string;
  name: string;
  version: string;
  performedAt: string;
  performedBy: { actorId: string; actorType: string; displayName: string };
  inputEvidenceIds?: string[];
}

export interface CitingFindingRef {
  findingId: string;
  title: string;
  severity?: SeverityState;
}

export interface EvidenceDetail {
  id: string;
  kind?: string;
  sourceSystem?: string;
  toolVersion?: string;
  capturedAt?: string;
  content: string;
  contentType?: 'log' | 'http' | 'diff' | 'code';
  expected?: string;
  observed?: string;
  limitations?: string;
  redactions?: Array<{ token: string; label: string; unmaskedValue?: string }>;
  // Provenance & cryptographic integrity (FE-042)
  sourceRecordId?: string;
  sourcePointer?: string;
  sha256?: string;
  storageRef?: string;
  integrityState?: 'HASH_VERIFIED' | 'UNVERIFIED' | string;
  sourceClass?:
    'RAW_TOOL_OUTPUT' | 'HUMAN_OBSERVATION' | 'DETERMINISTIC_TRANSFORM' | 'MODEL_ASSISTED' | string;
  origin?: 'SOURCE_REPORTED' | 'ANALYST_AUTHORED' | 'MODEL_INFERRED' | string;
  targetContact?: boolean;
  transformations?: EvidenceTransformation[];
  citingFindings?: CitingFindingRef[];
  isAiInterpretation?: boolean;
  aiReasoning?: string;
  modelAttribution?: {
    provider: string;
    modelId: string;
    modelVersion: string;
    promptTemplateVersion: string;
  } | null;
}

export interface DecisionDetail {
  id: string;
  title: string;
  target?: string;
  question: string;
  rationale?: string;
  impact?: string;
  status:
    'PENDING_REVIEW' | 'NEEDS_EVIDENCE' | 'ANALYST_VERIFIED' | 'ANALYST_REJECTED' | 'ACCEPTED_RISK';
  decidedBy?: string;
  decidedAt?: string;
  canDecide?: boolean;
}

export interface InspectorProps {
  selection: InspectorSelection;
  run?: RunView | null;
  events?: readonly RunEvent[];
  finding?: FindingDetail | null;
  evidence?: EvidenceDetail | null;
  decision?: DecisionDetail | null;
  onClose: () => void;
  /** Presentation: 'dock' | 'drawer' | 'full' drives layout and chrome. */
  mode: 'dock' | 'drawer' | 'full';
  onSelectEvidence?: (evidenceId: string) => void;
  onSelectFinding?: (findingId: string) => void;
  onDecide?: (action: 'ANALYST_VERIFIED' | 'ANALYST_REJECTED' | 'NEEDS_EVIDENCE') => void;
  onUndoDecision?: () => void;
}

/** "NETWORK_ENDPOINT" → "Network endpoint". */
function evidenceKindLabel(kind: string | undefined) {
  const text = (kind ?? 'evidence').replace(/_/g, ' ').toLowerCase();
  return (text.charAt(0).toUpperCase() + text.slice(1)).replace(/^Dns\b/, 'DNS');
}

function notAvailable(what: string, why: string) {
  return (
    <p className={styles.unavailable}>
      {what} isn’t available from the backend yet. {why}
    </p>
  );
}

export function Inspector({
  selection,
  run = null,
  events = [],
  finding = null,
  evidence = null,
  decision = null,
  onClose,
  mode,
  onSelectEvidence,
  onSelectFinding,
  onDecide,
  onUndoDecision,
}: InspectorProps) {
  const event = useMemo(
    () => (selection.kind === 'event' ? (events.find((e) => e.id === selection.id) ?? null) : null),
    [events, selection],
  );

  const step = useMemo(
    () =>
      run && selection.kind === 'check'
        ? (run.steps.find((s) => s.id === selection.id) ?? null)
        : null,
    [run, selection],
  );

  const title =
    selection.kind === 'event'
      ? event
        ? `Event at ${eventTime(event.at)}${event.stepId ? ` · ${event.stepId}` : ''}`
        : `Event ${selection.id}`
      : selection.kind === 'check'
        ? step
          ? step.id
          : selection.id
        : selection.kind === 'evidence'
          ? (evidence?.content ?? `Evidence ${shortId(selection.id)}`)
          : selection.kind === 'scope'
            ? `Run scope ${selection.id}`
            : selection.kind === 'plan'
              ? `Plan revision ${selection.id}`
              : selection.kind === 'decision'
                ? `Decision ${selection.id}`
                : (finding?.title ?? `Finding ${shortId(selection.id)}`);

  const content = (
    <>
      <header className={styles.header}>
        {mode === 'full' && (
          <Button variant="tertiary" size="small" onPress={onClose} className={styles.back}>
            <ArrowLeft size={16} aria-hidden="true" />
            <span>Back</span>
          </Button>
        )}
        <div className={styles.heading}>
          <h2 className={styles.title}>{title}</h2>
        </div>
        <Button variant="secondary" size="small" onPress={onClose} aria-label="Close inspector">
          <X size={18} aria-hidden="true" />
        </Button>
      </header>

      <Tabs defaultSelectedKey="summary" className={styles.tabs}>
        <TabList aria-label="Inspector sections">
          <Tab id="summary">Summary</Tab>
          <Tab id="evidence">Evidence</Tab>
          <Tab id="activity">Activity</Tab>
        </TabList>

        <TabPanel id="summary" className={styles.panel}>
          <div className={styles.body}>
            {/* 1. Finding selection */}
            {selection.kind === 'finding' && finding && (
              <>
                <div className={styles.entityHeader}>
                  <StateLabel kind="severity" state={finding.severity} />
                  {onSelectFinding && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onPress={() => onSelectFinding(finding.id)}
                    >
                      Open finding
                    </Button>
                  )}
                </div>
                <h3 className={styles.sectionHeading}>{finding.title}</h3>
                <div className={styles.truthGrid}>
                  <TruthLabel dimension="origin" state={finding.origin} />
                  <TruthLabel dimension="review" state={finding.review} />
                  {finding.remediation && (
                    <TruthLabel dimension="remediation" state={finding.remediation} />
                  )}
                  {finding.retest && <TruthLabel dimension="retest" state={finding.retest} />}
                </div>

                <dl className={styles.facts}>
                  <div>
                    <dt>Affected assets</dt>
                    <dd>{finding.affectedAssets.join(', ')}</dd>
                  </div>
                  <div>
                    <dt>Observation</dt>
                    <dd>{finding.description}</dd>
                  </div>
                  {finding.expected && (
                    <div>
                      <dt>Expected behavior</dt>
                      <dd>{finding.expected}</dd>
                    </div>
                  )}
                  {finding.observed && (
                    <div>
                      <dt>Observed behavior</dt>
                      <dd>{finding.observed}</dd>
                    </div>
                  )}
                  {finding.limitations && (
                    <div>
                      <dt>Limitations</dt>
                      <dd>{finding.limitations}</dd>
                    </div>
                  )}
                </dl>

                {/* Review actions only where a page records the decision; never a dead control. */}
                {onDecide &&
                (finding.review === 'UNREVIEWED' || finding.review === 'NEEDS_EVIDENCE') ? (
                  <div className={styles.reviewActionArea}>
                    <p className={styles.actionPrompt}>Record analyst review decision:</p>
                    <div className={styles.actionBtnRow}>
                      <Button
                        variant="primary"
                        size="small"
                        onPress={() => onDecide?.('ANALYST_VERIFIED')}
                      >
                        <Check size={14} aria-hidden="true" />
                        <span>Verify claim</span>
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onPress={() => onDecide?.('ANALYST_REJECTED')}
                      >
                        <X size={14} aria-hidden="true" />
                        <span>Reject claim</span>
                      </Button>
                      <Button
                        variant="tertiary"
                        size="small"
                        onPress={() => onDecide?.('NEEDS_EVIDENCE')}
                      >
                        <HelpCircle size={14} aria-hidden="true" />
                        <span>Needs evidence</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  onUndoDecision && (
                    <div className={styles.undoArea}>
                      <Button variant="secondary" size="small" onPress={onUndoDecision}>
                        <span>Undo decision</span>
                      </Button>
                      <span className={styles.undoNote}>
                        Undo appends a reversal event; history is preserved
                      </span>
                    </div>
                  )
                )}
              </>
            )}
            {selection.kind === 'finding' &&
              !finding &&
              notAvailable(
                'Finding detail',
                'No detailed finding record was supplied for this ID; truth dimensions remain unverified.',
              )}

            {/* 2. Evidence selection (FE-042: Full provenance & raw vs AI distinction) */}
            {selection.kind === 'evidence' && evidence && (
              <>
                <div className={styles.entityHeader}>
                  {evidence.kind && (
                    <span className={styles.kindBadge}>{evidenceKindLabel(evidence.kind)}</span>
                  )}
                  {evidence.isAiInterpretation ? (
                    <span className={styles.aiBadge}>
                      <Sparkles size={12} aria-hidden="true" />
                      <span>AI interpretation</span>
                    </span>
                  ) : (
                    <span className={styles.rawBadge}>
                      <ShieldCheck size={12} aria-hidden="true" />
                      <span>
                        {evidence.sourceClass === 'RAW_TOOL_OUTPUT'
                          ? 'Raw tool output'
                          : 'Source reported'}
                      </span>
                    </span>
                  )}
                </div>

                {/* The observation itself comes first; the provenance around it follows. */}
                <h4 className={styles.subHeading}>What was observed</h4>
                <EvidenceExcerpt {...evidence} embedded />

                {/* Plain terms first: when, by what, how, and whether anything touched the target. */}
                <dl className={styles.facts}>
                  {evidence.capturedAt && (
                    <div>
                      <dt>Captured</dt>
                      <dd>
                        <time dateTime={evidence.capturedAt} title={evidence.capturedAt}>
                          {formatDateTime(evidence.capturedAt)}
                        </time>
                      </dd>
                    </div>
                  )}
                  {(evidence.sourceSystem || evidence.toolVersion) && (
                    <div>
                      <dt>Source system &amp; collector</dt>
                      <dd>{collectorText(evidence.sourceSystem, evidence.toolVersion)}</dd>
                    </div>
                  )}
                  <div className={styles.wide}>
                    <dt>Provenance status</dt>
                    <dd>
                      {evidence.isAiInterpretation
                        ? 'Synthesized by AI inference model; not raw ground truth.'
                        : evidence.sourceClass === 'RAW_TOOL_OUTPUT'
                          ? 'Collected directly from the source tool. Unaltered raw output.'
                          : 'Source-reported record; this excerpt is not an AI interpretation.'}
                    </dd>
                  </div>
                  <div>
                    <dt>Transformations</dt>
                    <dd>
                      {evidence.transformations && evidence.transformations.length > 0 ? (
                        <ol className={styles.transformationList}>
                          {evidence.transformations.map((t) => (
                            <li key={t.transformationId}>
                              <strong>{t.name}</strong> ({t.class}) · v{t.version} by{' '}
                              {t.performedBy.displayName} at {t.performedAt}
                            </li>
                          ))}
                        </ol>
                      ) : evidence.sourceClass === 'RAW_TOOL_OUTPUT' ? (
                        '0 transformations · Direct unaltered output from collection tool'
                      ) : (
                        'No transformations recorded.'
                      )}
                    </dd>
                  </div>
                  {evidence.targetContact !== undefined && (
                    <div>
                      <dt>Target contact</dt>
                      <dd>
                        {evidence.targetContact
                          ? 'Active contact performed'
                          : 'False (passive observation only; no network contact)'}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* Ordered provenance trail (Storyboard 2): Claim → Evidence → Source */}
                <nav className={styles.provenanceTrail} aria-label="Evidence provenance trace">
                  <div className={styles.trailStep}>
                    <span className={styles.trailStepLabel}>Claim</span>
                    {evidence.citingFindings && evidence.citingFindings.length > 0 ? (
                      (() => {
                        const firstRef = evidence.citingFindings[0];
                        if (!firstRef)
                          return <span className={styles.trailValue}>No citing claim</span>;
                        return (
                          <button
                            type="button"
                            className={styles.trailLink}
                            onClick={() => onSelectFinding?.(firstRef.findingId)}
                            title={`Jump to claim ${firstRef.findingId}`}
                          >
                            {firstRef.title}
                          </button>
                        );
                      })()
                    ) : (
                      <span className={styles.trailValue}>No citing claim</span>
                    )}
                  </div>
                  <span className={styles.trailSeparator} aria-hidden="true">
                    →
                  </span>
                  <div className={styles.trailStep}>
                    <span className={styles.trailStepLabel}>Evidence</span>
                    <span className={styles.trailCurrent} title={evidence.id}>
                      {evidenceKindLabel(evidence.kind)}
                    </span>
                  </div>
                  <span className={styles.trailSeparator} aria-hidden="true">
                    →
                  </span>
                  <div className={styles.trailStep}>
                    <span className={styles.trailStepLabel}>Source</span>
                    <span className={styles.trailValue} title={evidence.sourceRecordId}>
                      {evidence.sourceSystem
                        ? collectorLabel(evidence.sourceSystem)
                        : 'Source record'}
                    </span>
                  </div>
                </nav>

                {/* Identifiers and hashes: exact, copyable, and out of the way until needed. */}
                <Disclosure title="Technical details" level={4} bare>
                  <dl className={styles.facts}>
                    <div className={styles.wide}>
                      <dt>Evidence ID</dt>
                      <dd>
                        <code>{evidence.id}</code>
                      </dd>
                    </div>
                    {evidence.sourcePointer && (
                      <div>
                        <dt>Source pointer</dt>
                        <dd>
                          <code>{neutralPointer(evidence.sourcePointer)}</code>
                        </dd>
                      </div>
                    )}
                    {evidence.sourceRecordId && (
                      <div className={styles.wide}>
                        <dt>Source record ID</dt>
                        <dd>
                          <code>{evidence.sourceRecordId}</code>
                        </dd>
                      </div>
                    )}
                    {evidence.sha256 && (
                      <div className={styles.wide}>
                        <dt>Source checksum</dt>
                        <dd>
                          <span className={styles.verifiedHash}>
                            <ShieldCheck size={14} aria-hidden="true" />
                            <code>sha256:{evidence.sha256}</code>
                          </span>
                          <span className={styles.hashStatus}>
                            {evidence.integrityState === 'HASH_VERIFIED'
                              ? 'Hash verified'
                              : 'Supplied checksum'}
                          </span>
                        </dd>
                      </div>
                    )}
                    {evidence.storageRef && (
                      <div className={styles.wide}>
                        <dt>Storage reference</dt>
                        <dd>
                          <code>{evidence.storageRef}</code>
                        </dd>
                      </div>
                    )}
                    {evidence.capturedAt && (
                      <div>
                        <dt>Captured timestamp</dt>
                        <dd>
                          <time dateTime={evidence.capturedAt}>{evidence.capturedAt}</time>
                        </dd>
                      </div>
                    )}
                  </dl>
                </Disclosure>

                {/* Citing Findings section */}
                {evidence.citingFindings && evidence.citingFindings.length > 0 && (
                  <div className={styles.citingClaimsSection}>
                    <h4 className={styles.subHeading}>Citing claims</h4>
                    <div className={styles.citingList}>
                      {evidence.citingFindings.map((cf) => (
                        <div key={cf.findingId} className={styles.citingItem}>
                          <div className={styles.citingItemLeft}>
                            {cf.severity && (
                              <StateLabel kind="severity" state={cf.severity} compact />
                            )}
                            <code className={styles.entityId}>{cf.findingId}</code>
                            <span className={styles.citingTitle}>{cf.title}</span>
                          </div>
                          {onSelectFinding && (
                            <Button
                              variant="tertiary"
                              size="small"
                              onPress={() => onSelectFinding(cf.findingId)}
                              aria-label={`View claim ${cf.findingId}`}
                            >
                              <span>View claim</span>
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {selection.kind === 'evidence' &&
              !evidence &&
              notAvailable(
                'Evidence detail',
                'The artifact body cannot be shown here yet, which limits what this selection can support.',
              )}

            {/* 3. Decision selection */}
            {selection.kind === 'decision' && decision && (
              <>
                <div className={styles.entityHeader}>
                  <code className={styles.entityId}>{decision.id}</code>
                  <StateLabel kind="attention" state="NEEDS_EVIDENCE" compact />
                </div>
                <h3 className={styles.sectionHeading}>{decision.title}</h3>
                <dl className={styles.facts}>
                  {decision.target && (
                    <div>
                      <dt>Target Scope</dt>
                      <dd>{decision.target}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Review Question</dt>
                    <dd>{decision.question}</dd>
                  </div>
                  {decision.rationale && (
                    <div>
                      <dt>Why Judgment is Needed</dt>
                      <dd>{decision.rationale}</dd>
                    </div>
                  )}
                  {decision.impact && (
                    <div>
                      <dt>Impact</dt>
                      <dd>{decision.impact}</dd>
                    </div>
                  )}
                  {decision.decidedBy && (
                    <div>
                      <dt>Attribution</dt>
                      <dd>
                        Decided by {decision.decidedBy}
                        {decision.decidedAt ? ` at ${decision.decidedAt}` : ''}
                      </dd>
                    </div>
                  )}
                </dl>
              </>
            )}
            {selection.kind === 'decision' &&
              !decision &&
              notAvailable('Decision detail', 'No decision record was supplied for this ID.')}

            {/* 4. Event selection */}
            {selection.kind === 'event' && event && (
              <>
                <dl className={styles.facts}>
                  <div>
                    <dt>Event ID</dt>
                    <dd>{event.id}</dd>
                  </div>
                  <div>
                    <dt>Actor</dt>
                    <dd>{event.actor}</dd>
                  </div>
                  <div>
                    <dt>Time</dt>
                    <dd>
                      <time dateTime={event.at} title={event.at}>
                        {clockTime(event.at)}
                      </time>
                    </dd>
                  </div>
                  {event.stepId && (
                    <div>
                      <dt>Step</dt>
                      <dd>{event.stepId}</dd>
                    </div>
                  )}
                  {event.durationMs !== undefined && (
                    <div>
                      <dt>Duration</dt>
                      <dd>{event.durationMs} ms</dd>
                    </div>
                  )}
                </dl>
                <p className={styles.sentence}>{event.text}</p>
                {event.evidenceId && (
                  <p className={styles.sentence}>
                    Retained evidence: <code>{event.evidenceId}</code>
                  </p>
                )}
              </>
            )}
            {selection.kind === 'event' &&
              !event &&
              notAvailable(
                'This event',
                'It may belong to a page of history that is not loaded; the run continues.',
              )}

            {/* 5. Check selection */}
            {selection.kind === 'check' && step && (
              <>
                <p className={styles.sentence}>{step.question}</p>
                <Badge data-badge={step.state}>{STEP_STATE_LABEL[step.state]}</Badge>
                <dl className={styles.facts}>
                  <div>
                    <dt>Why this check</dt>
                    <dd>{step.question}</dd>
                  </div>
                  <div>
                    <dt>What it can establish</dt>
                    <dd>
                      The retained evidence for this step records what the product actually did;
                      conclusions stay with reviewed findings.
                    </dd>
                  </div>
                  <div>
                    <dt>What it would miss</dt>
                    <dd>
                      Steps outside this check’s scope are not exercised here; untested coverage
                      stays untested.
                    </dd>
                  </div>
                </dl>
              </>
            )}
            {selection.kind === 'check' &&
              !step &&
              notAvailable('This check', 'The backend did not return a step with this id.')}

            {/* 6. Scope selection */}
            {selection.kind === 'scope' && run && (
              <dl className={styles.facts}>
                <div>
                  <dt>Targets</dt>
                  <dd>{run.scope.targets.join(', ')}</dd>
                </div>
                <div>
                  <dt>Environment</dt>
                  <dd>{run.scope.environment}</dd>
                </div>
                <div>
                  <dt>Exclusions</dt>
                  <dd>{run.scope.exclusions.join(', ') || 'None recorded'}</dd>
                </div>
              </dl>
            )}
            {selection.kind === 'scope' &&
              !run &&
              notAvailable('The scope summary', 'No run is recorded for this engagement yet.')}

            {/* 7. Plan selection */}
            {selection.kind === 'plan' && run && (
              <dl className={styles.facts}>
                <div>
                  <dt>Revision</dt>
                  <dd>r{run.plan.revision}</dd>
                </div>
                {run.plan.reviewedAt && (
                  <div>
                    <dt>Reviewed</dt>
                    <dd>{run.plan.reviewedAt}</dd>
                  </div>
                )}
                {run.plan.steps.map((s) => (
                  <div key={s.id}>
                    <dt>{s.id}</dt>
                    <dd>{s.question}</dd>
                  </div>
                ))}
              </dl>
            )}
            {selection.kind === 'plan' &&
              !run &&
              notAvailable('The plan summary', 'No run is recorded for this engagement yet.')}
          </div>
        </TabPanel>

        <TabPanel id="evidence" className={styles.panel}>
          <div className={styles.body}>
            {selection.kind === 'finding' &&
            finding?.evidenceIds &&
            finding.evidenceIds.length > 0 ? (
              <div className={styles.evidenceList}>
                <p className={styles.sentence}>Cited evidence artifacts for this finding:</p>
                {finding.evidenceIds.map((evId) => (
                  <div key={evId} className={styles.evidenceListItem}>
                    <code className={styles.evidenceId}>{evId}</code>
                    {onSelectEvidence && (
                      <Button
                        variant="secondary"
                        size="small"
                        onPress={() => onSelectEvidence(evId)}
                      >
                        <FileText size={14} aria-hidden="true" />
                        <span>Inspect artifact</span>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : selection.kind === 'evidence' && evidence ? (
              <EvidenceExcerpt {...evidence} />
            ) : selection.kind === 'event' && event?.evidenceId ? (
              <>
                <p className={styles.sentence}>
                  Retained artifact <code>{event.evidenceId}</code> from this event.
                </p>
                {notAvailable(
                  'The artifact body',
                  'The backend does not serve artifact bodies in this job; the claim this event can support stays limited to the retained line.',
                )}
              </>
            ) : (
              notAvailable(
                'Evidence for this selection',
                'Nothing retained is linked to it in this job’s backend contract.',
              )
            )}
          </div>
        </TabPanel>

        <TabPanel id="activity" className={styles.panel}>
          <div className={styles.body}>
            {events
              .filter((e) =>
                selection.kind === 'event'
                  ? e.id === selection.id
                  : selection.kind === 'check'
                    ? e.stepId === selection.id
                    : selection.kind === 'evidence'
                      ? e.evidenceId === selection.id
                      : selection.kind === 'finding'
                        ? e.text.includes(selection.id)
                        : false,
              )
              .slice(0, 20)
              .map((e) => (
                <p key={e.id} className={styles.sentence}>
                  <code>{e.at}</code> · {e.actor} · {e.text}
                </p>
              ))}
            {events.filter((e) =>
              selection.kind === 'event'
                ? e.id === selection.id
                : selection.kind === 'check'
                  ? e.stepId === selection.id
                  : selection.kind === 'evidence'
                    ? e.evidenceId === selection.id
                    : selection.kind === 'finding'
                      ? e.text.includes(selection.id)
                      : false,
            ).length === 0 &&
              notAvailable('Related activity', 'No retained events reference this selection.')}
            <p className={styles.note}>
              Raw terminal lines stay in the terminal; this list shows only the retained events
              linked to the selection.
            </p>
          </div>
        </TabPanel>
      </Tabs>
    </>
  );

  // The docked pane never traps focus; the drawer and the full-screen view are
  // modal: focus contained, Escape closes, focus returns to the opener (§4.8).
  if (mode === 'dock') {
    return (
      <aside
        className={[styles.inspector, styles.dock].join(' ')}
        aria-label="Selection inspector"
        data-mode={mode}
      >
        {content}
      </aside>
    );
  }

  return (
    <ModalOverlay
      isOpen
      isDismissable={mode === 'drawer'}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className={[styles.scrim, styles[`${mode}Scrim`]].join(' ')}
    >
      <Modal className={[styles.inspector, styles[mode]].join(' ')}>
        <Dialog aria-label="Selection inspector" data-mode={mode} className={styles.dialog}>
          {content}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
